import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  IconArrowRight, IconChevronDown, IconFileDollar, IconFileText, IconFolders, IconHome, IconKey, IconLayoutKanban,
  IconListCheck, IconLogout, IconMenu2, IconMoon, IconNotebook, IconPlus, IconSearch, IconShield, IconShieldLock,
  IconSun, IconUpload, IconX,
} from '@tabler/icons-react'
import * as Dialog from '@radix-ui/react-dialog'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'

import { authApi, homeApi } from '@/api/endpoints'
import { useAuthStore } from '@/store/authStore'
import { useSystemCan } from '@/hooks/usePermissions'
import { ADMIN_PAGES, type AdminPage } from './adminPages'
import { BrandMark } from '@/components/common/BrandMark'
import { NotificationBell } from './NotificationBell'
import { tr } from '@/i18n/tr'
import {
  Avatar, Badge, Menu, MenuDivider, MenuItem, cx, isMac, useColorScheme, useHotkey, useMediaQuery,
} from '@/ui'
import { projectColor } from '../../../../packages/shared/src/tokens'
import './shell.css'

/**
 * Uygulama kabuğu: koyu kenar menü + üst çubuk + içerik.
 *
 * <p>Kenar menü her iki temada koyu kalır; gezinme içerikten renkle ayrılır. Dar
 * ekranda menü çekmeceye döner ve kapalıyken `inert` olur: klavyeyle Tab'a basan
 * kullanıcı görünmeyen bağlantılarda dolaşmaz, ekran okuyucu onları okumaz.
 */
export function AppLayout() {
  const [navOpen, setNavOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const { user, clear, isAdmin } = useAuthStore()
  const sysCan = useSystemCan()
  const adminPages = ADMIN_PAGES.filter((p) => sysCan(p.permission))
  const { scheme, toggle } = useColorScheme()
  const location = useLocation()
  const navigate = useNavigate()
  const narrow = useMediaQuery('(max-width: 899.98px)')
  const modKey = isMac() ? '⌘' : 'Ctrl'

  const home = useQuery({ queryKey: ['home'], queryFn: homeApi.summary, staleTime: 60_000 })

  useHotkey('mod+k', () => setPaletteOpen(true))
  useEffect(() => { setNavOpen(false) }, [location.pathname])

  const logout = async () => {
    try {
      await authApi.logout()
    } finally {
      clear()
      navigate('/login', { replace: true })
    }
  }

  const active = (path: string) => location.pathname.startsWith(path)
  const closeNav = () => setNavOpen(false)
  // React 18 `inert`'i tanımıyor; boş dize ile öznitelik olarak geçer.
  const navInert = narrow && !navOpen ? ({ inert: '' } as Record<string, string>) : {}

  return (
    <div className="nx-app">
      {narrow && navOpen && <button type="button" className="nx-nav-scrim" aria-label="Menüyü kapat" onClick={closeNav} />}
      <nav aria-label="Ana menü" className={cx('nx-nav', narrow && 'nx-nav--drawer', navOpen && 'nx-nav--open')} {...navInert}>
        <Link to="/" className="nx-nav-brand" onClick={closeNav}>
          <BrandMark />
          <span className="nx-nav-brand__text">
            <span className="nx-nav-brand__name">{tr.app.name}</span>
            <span className="nx-nav-brand__tag">Proje arşivi</span>
          </span>
        </Link>

        {sysCan('project.create') && (
          <Link to="/projeler?yeni=1" className="nx-nav-new" onClick={closeNav}>
            <IconPlus size={16} stroke={2.4} aria-hidden="true" />Yeni proje
          </Link>
        )}

        <div className="nx-nav-scroll">
          <NavSection title="Çalışma alanı">
            <NavItem to="/" label={tr.nav.home} icon={<IconHome size={18} stroke={1.8} />} active={location.pathname === '/'} onClick={closeNav} />
            <NavItem to="/projeler" label={tr.nav.projects} icon={<IconFolders size={18} stroke={1.8} />} active={active('/projeler')} onClick={closeNav}
              count={home.data?.stats.projects} />
            <NavItem to="/proje-durumlari" label={tr.nav.overview} icon={<IconLayoutKanban size={18} stroke={1.8} />} active={active('/proje-durumlari')} onClick={closeNav} />
            <NavItem to="/gorevler" label={tr.nav.allTasks} icon={<IconListCheck size={18} stroke={1.8} />} active={active('/gorevler')} onClick={closeNav} />
            <NavItem to="/teklifler" label={tr.nav.offers} icon={<IconFileDollar size={18} stroke={1.8} />} active={active('/teklifler')} onClick={closeNav} />
            <NavItem to="/arama" label={tr.nav.search} icon={<IconSearch size={18} stroke={1.8} />} active={active('/arama')} onClick={closeNav} />
          </NavSection>

          {(home.data?.activeProjects.length ?? 0) > 0 && (
            <NavSection title="Son projeler">
              {home.data!.activeProjects.slice(0, 3).map((p) => (
                <Link key={p.id} to={`/projeler/${p.id}`} className="nx-nav-recent" onClick={closeNav}
                  aria-current={location.pathname.startsWith(`/projeler/${p.id}`) ? 'page' : undefined}>
                  <span className="nx-nav-recent__dot-wrap"><span className="nx-nav-recent__dot" style={{ background: projectColor(p.id) }} /></span>
                  <span className="nx-nav-recent__name">{p.name}</span>
                </Link>
              ))}
            </NavSection>
          )}

          {adminPages.length > 0 && (
            <NavSection title={tr.nav.admin} icon={<IconShield size={12} stroke={2} aria-hidden="true" />}>
              {adminPages.map((p) => (
                <NavItem key={p.path} to={p.path} label={p.label} icon={p.icon} active={active(p.path)} onClick={closeNav} />
              ))}
            </NavSection>
          )}
        </div>

        <div className="nx-nav-footer">
          <span>{tr.app.name} · sürüm 0.1</span>
          <span className="nx-nav-kbd">{modKey}K</span>
        </div>
      </nav>

      <div className="nx-main">
        <header className="nx-topbar">
          {narrow && (
            <button type="button" className="nx-topbar__icon" aria-label={navOpen ? 'Menüyü kapat' : 'Menüyü aç'}
              aria-expanded={navOpen} onClick={() => setNavOpen((o) => !o)}>
              {navOpen ? <IconX size={20} /> : <IconMenu2 size={20} />}
            </button>
          )}
          <button type="button" className="nx-topbar__search" onClick={() => setPaletteOpen(true)} aria-label="Genel arama"
            aria-haspopup="dialog">
            <IconSearch size={18} stroke={1.8} aria-hidden="true" />
            <span className="nx-topbar__search-text">{tr.search.placeholder}</span>
            <kbd className="nx-topbar__kbd">{modKey}K</kbd>
          </button>
          <span className="nx-topbar__spacer" />
          <NotificationBell />
          <button type="button" className="nx-topbar__icon" onClick={toggle} aria-label="Tema değiştir"
            title={scheme === 'dark' ? 'Açık tema' : 'Koyu tema'}>
            {scheme === 'dark' ? <IconSun size={19} stroke={1.8} /> : <IconMoon size={19} stroke={1.8} />}
          </button>
          <Menu width={268} trigger={
            <button type="button" className="nx-account" aria-label="Hesap menüsü">
              <Avatar name={user?.displayName ?? '?'} size={34} dark />
              <span className="nx-account__text">
                <span className="nx-account__name">{user?.displayName}</span>
                <span className="nx-account__user">@{user?.username}</span>
              </span>
              <IconChevronDown size={16} stroke={2} className="nx-account__caret" aria-hidden="true" />
            </button>
          }>
            <div className="nx-account__head">
              <Avatar name={user?.displayName ?? '?'} size={40} dark />
              <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{user?.displayName}</span>
                <span style={{ fontSize: 12, color: 'var(--nx-fg-muted)' }}>@{user?.username}</span>
              </span>
              {isAdmin() && <Badge tone="ink" size="sm" style={{ marginLeft: 'auto', color: 'var(--nx-lime)' }}>Yönetici</Badge>}
            </div>
            <MenuItem asChild><Link to="/guvenlik"><IconShieldLock size={17} stroke={1.8} />Profil ve güvenlik</Link></MenuItem>
            <MenuItem asChild><Link to="/parola-degistir"><IconKey size={17} stroke={1.8} />{tr.auth.changePassword}</Link></MenuItem>
            <MenuDivider />
            <MenuItem danger icon={<IconLogout size={17} stroke={1.8} />} onSelect={logout}>{tr.auth.logout}</MenuItem>
          </Menu>
        </header>

        <main className="nx-content" id="icerik">
          {/* Proje sayfaları tam genişlikte başlık bandını ve kendi kabını kurar. */}
          {/^\/projeler\/[^/]+/.test(location.pathname)
            ? <Outlet />
            : <div className="nx-page"><Outlet /></div>}
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} recent={home.data?.recent ?? []}
        adminPages={adminPages} canCreateProject={sysCan('project.create')} />
    </div>
  )
}

function NavSection({ title, icon, children }: { title: string; icon?: ReactNode; children: ReactNode }) {
  return (
    <div className="nx-nav-section">
      <div className="nx-nav-section__title">{title}{icon}</div>
      {children}
    </div>
  )
}

/** Kenar menüdeki tek bağlantı. Sınıf ve data-active, testlerin seçicisi olarak korunur. */
function NavItem({
  to, label, icon, active, onClick, count,
}: { to: string; label: string; icon: ReactNode; active: boolean; onClick: () => void; count?: number }) {
  return (
    <Link to={to} onClick={onClick} className="nx-nav-link" data-active={active || undefined} aria-current={active ? 'page' : undefined}>
      <span className="nx-nav-link__icon">{icon}</span>
      <span className="nx-nav-link__label">{label}</span>
      {count !== undefined && <span className="nx-nav-link__count">{count}</span>}
    </Link>
  )
}

/* ---------------------------------------------------------------- komut paleti */

interface PaletteItem { key: string; label: string; sub: string; to: string; icon: ReactNode; tile: string; tileFg: string; group: string }

function CommandPalette({
  open, onClose, recent, adminPages, canCreateProject,
}: {
  open: boolean
  onClose: () => void
  recent: Array<{ type: 'NOTE' | 'FILE'; id: string; projectId: string; projectName: string; name: string }>
  adminPages: AdminPage[]
  canCreateProject: boolean
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const [q, setQ] = useState('')
  const [activeIdx, setActiveIdx] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)
  const projectMatch = location.pathname.match(/^\/projeler\/([0-9a-f-]{36})/)

  const items = useMemo<PaletteItem[]>(() => {
    const quick: PaletteItem[] = canCreateProject
      ? [{ key: 'new-project', group: 'Hızlı işlemler', label: 'Yeni proje', sub: 'Proje oluştur ve görünürlüğünü seç', to: '/projeler?yeni=1', icon: <IconPlus size={16} />, tile: 'var(--nx-lime)', tileFg: 'var(--nx-ink)' }]
      : []
    if (projectMatch) {
      quick.push(
        { key: 'new-note', group: 'Hızlı işlemler', label: 'Yeni not', sub: 'Bu proje içinde', to: `/projeler/${projectMatch[1]}/notlar?yeni=1`, icon: <IconNotebook size={16} />, tile: 'var(--nx-surface-sunken)', tileFg: 'var(--nx-fg)' },
        { key: 'upload', group: 'Hızlı işlemler', label: 'Dosya yükle', sub: 'Word, Excel, PDF · en fazla 100 MB', to: `/projeler/${projectMatch[1]}/dosyalar`, icon: <IconUpload size={16} />, tile: 'var(--nx-surface-sunken)', tileFg: 'var(--nx-fg)' },
      )
    }
    const rec: PaletteItem[] = recent.slice(0, 5).map((r) => ({
      key: `${r.type}-${r.id}`, group: 'Son açılanlar', label: r.name || 'Başlıksız not',
      sub: `${r.type === 'NOTE' ? 'Not' : 'Dosya'} · ${r.projectName}`,
      to: r.type === 'NOTE' ? `/projeler/${r.projectId}/notlar/${r.id}` : `/projeler/${r.projectId}/dosyalar`,
      icon: r.type === 'NOTE' ? <IconNotebook size={16} /> : <IconFileText size={16} />,
      tile: r.type === 'NOTE' ? 'var(--nx-info-bg)' : 'var(--nx-success-bg)', tileFg: r.type === 'NOTE' ? 'var(--nx-info-fg)' : 'var(--nx-success-fg)',
    }))
    const nav: PaletteItem[] = [
      { key: 'go-projects', group: 'Gezin', label: tr.nav.projects, sub: 'Tüm projeleriniz', to: '/projeler', icon: <IconArrowRight size={16} />, tile: 'var(--nx-surface-sunken)', tileFg: 'var(--nx-fg)' },
      { key: 'go-tasks', group: 'Gezin', label: tr.nav.allTasks, sub: 'Erişiminiz olan projelerdeki görevler', to: '/gorevler', icon: <IconArrowRight size={16} />, tile: 'var(--nx-surface-sunken)', tileFg: 'var(--nx-fg)' },
      { key: 'go-offers', group: 'Gezin', label: tr.nav.offers, sub: 'Bütün projelerdeki teklifler ve raporu', to: '/teklifler', icon: <IconArrowRight size={16} />, tile: 'var(--nx-surface-sunken)', tileFg: 'var(--nx-fg)' },
      { key: 'go-search', group: 'Gezin', label: tr.nav.search, sub: 'Notlar ve dosyalar', to: '/arama', icon: <IconArrowRight size={16} />, tile: 'var(--nx-surface-sunken)', tileFg: 'var(--nx-fg)' },
    ]
    adminPages.forEach((p) => nav.push({ key: `go-${p.path}`, group: 'Gezin', label: p.label, sub: 'Yönetim', to: p.path, icon: <IconArrowRight size={16} />, tile: 'var(--nx-surface-sunken)', tileFg: 'var(--nx-fg)' }))
    const all = [...quick, ...rec, ...nav]
    const needle = q.trim().toLocaleLowerCase('tr')
    return needle ? all.filter((i) => (i.label + ' ' + i.sub).toLocaleLowerCase('tr').includes(needle)) : all
  }, [q, recent, adminPages, canCreateProject, projectMatch])

  useEffect(() => { if (open) { setQ(''); setActiveIdx(0) } }, [open])
  useEffect(() => { setActiveIdx(0) }, [q])

  const go = (to: string) => { onClose(); navigate(to) }
  const searchAll = () => { const v = q.trim(); if (v) go(`/arama?q=${encodeURIComponent(v)}`) }
  // Aramaya düşen satır en sonda: yazılan metin bir öğeyle eşleşmese de Enter işe yarar.
  const total = items.length + (q.trim() ? 1 : 0)

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(total - 1, i + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)) }
    else if (e.key === 'Enter') {
      e.preventDefault()
      if (activeIdx < items.length) go(items[activeIdx].to); else searchAll()
    }
  }

  let lastGroup = ''
  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="ui-overlay" />
        <Dialog.Content className="nx-palette" aria-describedby={undefined}>
          <Dialog.Title className="ui-visually-hidden">Komut paleti</Dialog.Title>
          <div className="nx-palette__head">
            <IconSearch size={20} stroke={1.8} aria-hidden="true" style={{ color: 'var(--nx-fg-muted)' }} />
            <input autoFocus aria-label="Ara ya da komut yaz" placeholder="Ara ya da bir komut yazın…" value={q}
              role="combobox" aria-expanded="true" aria-controls="nx-palette-list"
              aria-activedescendant={total ? `nx-pal-${activeIdx}` : undefined}
              onChange={(e) => setQ(e.currentTarget.value)} onKeyDown={onKey} />
            <Dialog.Close className="nx-palette__esc">esc</Dialog.Close>
          </div>
          <div className="nx-palette__list" id="nx-palette-list" role="listbox" ref={listRef}>
            {items.map((it, i) => {
              const head = it.group !== lastGroup ? it.group : null
              lastGroup = it.group
              return (
                <div key={it.key}>
                  {head && <div className="nx-palette__group">{head}</div>}
                  <div id={`nx-pal-${i}`} role="option" aria-selected={i === activeIdx} className="nx-palette__item"
                    data-active={i === activeIdx} onMouseEnter={() => setActiveIdx(i)} onClick={() => go(it.to)}>
                    <span className="nx-palette__tile" style={{ background: it.tile, color: it.tileFg }}>{it.icon}</span>
                    <span className="nx-palette__text"><span className="nx-palette__label">{it.label}</span><span className="nx-palette__sub">{it.sub}</span></span>
                    {i === activeIdx && <span className="nx-palette__hint">↵</span>}
                  </div>
                </div>
              )
            })}
            {q.trim() && (
              <div id={`nx-pal-${items.length}`} role="option" aria-selected={activeIdx === items.length} className="nx-palette__item"
                data-active={activeIdx === items.length} onMouseEnter={() => setActiveIdx(items.length)} onClick={searchAll}>
                <span className="nx-palette__tile" style={{ background: 'var(--nx-surface-sunken)', color: 'var(--nx-fg)' }}><IconSearch size={16} /></span>
                <span className="nx-palette__text"><span className="nx-palette__label">"{q.trim()}" için ara</span><span className="nx-palette__sub">Notlarda ve dosyalarda</span></span>
              </div>
            )}
          </div>
          <div className="nx-palette__foot"><span>↑↓ gezin</span><span>↵ aç</span><span>esc kapat</span></div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
